import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

/**
 * Shimmer placeholder shown while the persisted agent is being fetched from
 * `agentStudio_agentById`. Visually approximates the final card's silhouette
 * (avatar + name line + body lines + tool pill row) so the layout doesn't
 * jump once hydration completes.
 *
 * The skeleton intentionally avoids rendering `<AgentAvatar>` because that
 * component eagerly resolves a per-agent avatar SVG chunk via dynamic
 * `import()` — substituting a static placeholder keeps the loading state
 * quick and free of lazy-chunk failures.
 *
 * Each placeholder shape uses Atlaskit's `<Skeleton>` primitive which
 * provides the standard shimmer animation when `isShimmering` is set.
 */
const styles = cssMap({
	card: {
		width: '100%',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.medium'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
	},
});

export const CreateAgentCardSkeleton = (): React.JSX.Element => {
	return (
		<Box xcss={styles.card} testId="rovo-create-agent-card-skeleton">
			<Stack space="space.150">
				<Flex alignItems="center" gap="space.100">
					<Skeleton width={32} height={32} borderRadius={token('radius.full')} isShimmering />
					<Skeleton width="60%" height={14} isShimmering />
				</Flex>
				<Stack space="space.075">
					<Skeleton width="100%" height={10} isShimmering />
					<Skeleton width="100%" height={10} isShimmering />
					<Skeleton width="75%" height={10} isShimmering />
				</Stack>
				<Inline space="space.100">
					<Skeleton width={72} height={20} isShimmering />
					<Skeleton width={72} height={20} isShimmering />
					<Skeleton width={72} height={20} isShimmering />
				</Inline>
			</Stack>
		</Box>
	);
};
